/* ShareCampaignQrDialog — dashboard-side share sheet for the /demo QR.
   -------------------------------------------------------------------
   Fetches the caller's referral code + lifetime stats from
   `GET /users/me/referrals`, encodes the QR as `<origin>/demo?ref=<code>`,
   and surfaces "invited X · earned ₦Y" so the resident sees the growth
   loop paying off. Both the QR and the sharer/invitee get ₦100 free
   credit when someone new signs up via this link.

   Kept in its own file so we can drop this trigger anywhere else on the
   dashboard later without duplicating the QR/copy/download plumbing. */
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import { Copy, Check, Share2, Megaphone, ExternalLink, Users, Wallet, Loader2 } from "lucide-react";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ShareCampaignQrDialog({ open, onClose }) {
  const [copied, setCopied] = useState(false);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  // Pull the caller's referral code + stats the first time the dialog
  // opens. Cached in state for the life of the dialog — closing +
  // reopening refetches so a fresh signup shows up without a page
  // reload. Non-blocking; if the request fails we still let the user
  // share a code-less demo URL (they just don't earn the referral
  // bonus in that edge case).
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const r = await axios.get(`${API}/users/me/referrals`, { withCredentials: true });
        if (!cancelled) setStats(r.data);
      } catch (e) {
         
        console.warn("[ShareCampaignQrDialog] fetch failed", e?.response?.data || e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  // Canonical share URL. Only appends `?ref=<code>` once we've loaded
  // the code — the QR re-renders in place when it arrives.
  const demoUrl = useMemo(() => {
    if (typeof window === "undefined") return "";
    const base = `${window.location.origin}/demo`;
    return stats?.code ? `${base}?ref=${encodeURIComponent(stats.code)}` : base;
  }, [stats]);

  const handleCopyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(demoUrl);
      setCopied(true);
      toast.success("Link copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy — long-press the link to share.");
    }
  }, [demoUrl]);

  const handleNativeShare = useCallback(async () => {
    if (!navigator.share) {
      handleCopyLink();
      return;
    }
    try {
      await navigator.share({
        title: "Guard Scan2Call — try the demo",
        text: `Turn any door into a video-call intercom. Sign up via my link and we both get ₦${stats?.bonus_ngn ?? 100} free wallet credit:`,
        url: demoUrl,
      });
    } catch {
      // User cancelled — no toast needed.
    }
  }, [demoUrl, handleCopyLink, stats]);

  const downloadQr = useCallback(() => {
    const canvas = document.querySelector('canvas[data-testid="campaign-qr-canvas"]');
    if (!canvas) return;
    const url = canvas.toDataURL("image/png");
    const a = document.createElement("a");
    a.href = url;
    a.download = "guard-scan2call-demo-qr.png";
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast.success("QR downloaded — print & share!");
  }, []);

  const openDemoPreview = useCallback(() => {
    window.open(demoUrl, "_blank", "noopener,noreferrer");
  }, [demoUrl]);

  const bonus = stats?.bonus_ngn ?? 100;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="sm:max-w-md rounded-sm" data-testid="share-campaign-dialog">
        <DialogTitle className="flex items-center gap-2 font-heading text-xl font-black tracking-tighter">
          <Megaphone className="w-5 h-5 text-emerald-600" /> Share campaign QR
        </DialogTitle>
        <DialogDescription className="text-xs text-gray-500 font-body">
          When someone signs up via your link, you BOTH get{" "}
          <span className="font-bold text-emerald-700">₦{bonus} free wallet credit</span>.
          They land on the Guard demo, try it live, then get invited to join.
        </DialogDescription>

        {/* Stats strip — invited / earned. Rendered as a two-column pill
            so the numbers pop even at a glance. Shows a subtle spinner
            while the endpoint is loading; hides entirely on error so we
            don't dead-space the dialog for offline / brand-new users. */}
        {(loading || stats) && (
          <div
            className="grid grid-cols-2 gap-2 mt-1"
            data-testid="campaign-stats"
          >
            <div className="bg-emerald-50 border border-emerald-200 rounded-sm px-3 py-2 flex items-center gap-2">
              <Users className="w-4 h-4 shrink-0 text-emerald-600" />
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-widest font-bold text-emerald-700 font-body">Invited</p>
                <p className="font-heading text-lg font-black tracking-tighter leading-none" data-testid="campaign-invited-count">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin inline" /> : (stats?.count ?? 0)}
                </p>
              </div>
            </div>
            <div className="bg-indigo-50 border border-indigo-200 rounded-sm px-3 py-2 flex items-center gap-2">
              <Wallet className="w-4 h-4 shrink-0 text-indigo-600" />
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-widest font-bold text-indigo-700 font-body">Earned</p>
                <p className="font-heading text-lg font-black tracking-tighter leading-none" data-testid="campaign-earned-ngn">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin inline" /> : `₦${(stats?.earned_ngn ?? 0).toLocaleString()}`}
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="bg-white p-4 rounded-md border border-gray-200 flex items-center justify-center mt-1">
          <QRCodeCanvas
            value={demoUrl}
            size={220}
            level="H"
            includeMargin={false}
            bgColor="#ffffff"
            fgColor="#000000"
            imageSettings={{
              src: "/icons/icon-192.png",
              width: 46,
              height: 46,
              excavate: true,
            }}
            data-testid="campaign-qr-canvas"
          />
        </div>

        <div className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-sm px-2.5 py-2">
          <span className="text-[11px] font-mono text-gray-700 truncate mr-2" data-testid="campaign-share-url">
            {demoUrl}
          </span>
          <button
            type="button"
            onClick={handleCopyLink}
            className="shrink-0 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-body font-bold text-emerald-600 hover:text-emerald-700"
            data-testid="campaign-copy-link"
          >
            {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
            {copied ? "Copied" : "Copy"}
          </button>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <Button
            onClick={downloadQr}
            className="h-11 bg-emerald-500 hover:bg-emerald-600 text-black font-body font-bold rounded-sm"
            data-testid="campaign-download-qr"
          >
            Download PNG
          </Button>
          <Button
            onClick={handleNativeShare}
            variant="outline"
            className="h-11 border-emerald-400/60 text-emerald-700 hover:bg-emerald-50 rounded-sm font-body font-bold"
            data-testid="campaign-native-share"
          >
            <Share2 className="w-4 h-4 mr-1.5" /> Share…
          </Button>
        </div>

        <button
          type="button"
          onClick={openDemoPreview}
          className="inline-flex items-center justify-center gap-1 text-[10px] uppercase tracking-widest font-body font-bold text-gray-500 hover:text-gray-700 pt-1"
          data-testid="campaign-preview-demo"
        >
          Preview the demo yourself <ExternalLink className="w-3 h-3" />
        </button>
      </DialogContent>
    </Dialog>
  );
}
